import { ClientCombobox } from "@/components/ClientCombobox";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useColumnResize } from "@/hooks/useColumnResize";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail, fmtDate } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PrintanistaMeterDialog } from "@/components/PrintanistaMeterDialog";
import { meterLabelIt } from "@/lib/meterLabels";
import { PrintanistaSuppliesDashboard } from "@/components/PrintanistaSuppliesDashboard";
import { PlusIcon as Plus, PencilSimpleIcon as PencilSimple, TrashIcon as Trash, GaugeIcon as Gauge, DropIcon as Drop, DesktopIcon as Desktop, WifiHighIcon as WifiHigh, ArrowsClockwiseIcon as ArrowsClockwise, WarningCircleIcon as WarningCircle, ProhibitIcon as Prohibit, ArrowCounterClockwiseIcon as ArrowCounterClockwise, DownloadSimpleIcon as DownloadSimple, LinkSimpleIcon as LinkSimple, UserPlusIcon as UserPlus, ReceiptIcon as Receipt, FileTextIcon as FileText, CaretUpIcon as CaretUp, CaretDownIcon as CaretDown, CaretUpDownIcon as CaretUpDown, MagnifyingGlassIcon as MagnifyingGlass, PaperclipIcon as Paperclip } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import NinjaDevices from "@/pages/NinjaDevices";
import ZyxelDevices from "@/pages/ZyxelDevices";
import { CopyCostStatements } from "@/components/CopyCostStatements";

const EMPTY = { cliente_id: "", contratto_id: "none", marca: "", modello: "", serial: "", ubicazione: "", tipo: "colore", stato: "attivo" };

const devSortVal = (d, k) => {
  switch (k) {
    case "cliente": return (d.cliente_nome || "").toLowerCase();
    case "dispositivo": return `${d.marca || ""} ${d.modello || ""}`.trim().toLowerCase();
    case "serial": return (d.serial || "").toLowerCase();
    case "ultima_lettura": return d.ultima_lettura?.data_lettura || "";
    case "stato": return d.stato || "";
    default: return "";
  }
};

// Valore di un contatore (per codice) alla data indicata: ultima lettura dello storico
// con reading_at <= data selezionata. 0 se non disponibile (es. nessun Printanista).
const valueAt = (readings, code, dateStr) => {
  if (!code || !dateStr || !Array.isArray(readings)) return 0;
  const cand = readings.filter((r) => r.remote_meter_code === code && String(r.reading_at).slice(0, 10) <= dateStr);
  if (!cand.length) return 0;
  cand.sort((a, b) => String(a.reading_at).localeCompare(String(b.reading_at)));
  return cand[cand.length - 1].value ?? 0;
};

// Date (yyyy-mm-dd) per cui esiste una lettura remota dei contatori mono/colore scelti.
const readingDates = (readings, codes) => {
  const set = new Set();
  (readings || []).forEach((r) => {
    if (r.remote_meter_code === codes.mono || r.remote_meter_code === codes.colore) set.add(String(r.reading_at).slice(0, 10));
  });
  return set;
};

const daysSince = (iso) => {
  if (!iso) return null;
  const d = new Date(String(iso).replace(" ", "T"));
  if (isNaN(d)) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};

export default function Devices() {
  const [devices, setDevices] = useState(null);
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]); // eslint-disable-line no-unused-vars
  const [searchParams] = useSearchParams();
  const [fCliente, setFCliente] = useState(() => searchParams.get("cliente") || "all");
  const [prtFilter, setPrtFilter] = useState(() => searchParams.get("prt") || "all");
  const [alerts, setAlerts] = useState(null);
  const [exclusions, setExclusions] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [meterDevice, setMeterDevice] = useState(null);
  const [meterSyncing, setMeterSyncing] = useState(false);
  const [suppliesOpen, setSuppliesOpen] = useState(false);
  const [deviceTab, setDeviceTab] = useState("stampanti");
  const [stmtDeviceId, setStmtDeviceId] = useState(null);
  const [stmtDeviceLabel, setStmtDeviceLabel] = useState("");
  const [unbill, setUnbill] = useState({ n_dispositivi: 0, items: [] });
  const [coverage, setCoverage] = useState({ senza_contratto_ids: [] });
  const noContractSet = new Set(coverage.senza_contratto_ids || []);
  const loadUnbill = useCallback(async () => {
    try { const { data } = await api.get("/copy-cost-contracts/unbillable-pages"); setUnbill(data || { items: [] }); }
    catch { /* ignore */ }
    try { const { data } = await api.get("/copy-cost-contracts/device-coverage"); setCoverage(data || { senza_contratto_ids: [] }); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { loadUnbill(); }, [loadUnbill]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [ccContracts, setCcContracts] = useState([]);
  const loadCcContracts = useCallback(async (clienteId) => {
    if (!clienteId) { setCcContracts([]); return; }
    try { const { data } = await api.get(`/copy-cost-contracts?cliente_id=${clienteId}`); setCcContracts(data || []); }
    catch { setCcContracts([]); }
  }, []);

  // readings
  const [readOpen, setReadOpen] = useState(false);
  const [readDevice, setReadDevice] = useState(null);
  const [readings, setReadings] = useState([]);
  const [rForm, setRForm] = useState({ data_lettura: "", contatore_bn: 0, contatore_colore: 0, note: "" });
  const [rFile, setRFile] = useState(null);
  const [meterHist, setMeterHist] = useState([]);
  const [meterCodes, setMeterCodes] = useState({ mono: null, colore: null });
  // toner logbook
  const [toners, setToners] = useState([]);
  const [tForm, setTForm] = useState({ data_consegna: "", contatore_bn: 0, contatore_colore: 0, tipo_toner: "", note: "" });

  const load = useCallback(() => {
    const params = {};
    if (fCliente !== "all") params.cliente_id = fCliente;
    api.get("/devices", { params }).then(({ data }) => setDevices(data));
  }, [fCliente]);

  const loadPrt = useCallback(() => {
    api.get("/integrations/printanista/alerts").then(({ data }) => setAlerts(data)).catch(() => setAlerts(null));
    api.get("/integrations/printanista/exclusions").then(({ data }) => setExclusions(data.exclusions || [])).catch(() => setExclusions([]));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPrt(); }, [loadPrt]);
  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/contracts").then(({ data }) => setContracts(data));
  }, []);
  useEffect(() => {
    const prt = searchParams.get("prt");
    const cli = searchParams.get("cliente");
    if (prt) setPrtFilter(prt);
    if (cli) setFCliente(cli);
    if (prt || cli) setDeviceTab("stampanti");
  }, [searchParams]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, cc_contract_id: "" }); setCcContracts([]); setOpen(true); };
  const openEdit = async (d) => {
    setEditing(d); setForm({ ...EMPTY, ...d, contratto_id: d.contratto_id || "none", cc_contract_id: "" }); setOpen(true);
    loadCcContracts(d.cliente_id);
    try { const { data } = await api.get(`/devices/${d.id}/copy-cost-link`); setForm((f) => ({ ...f, cc_contract_id: data.contract_id || "" })); } catch { /* ignore */ }
  };

  const save = async () => {
    if (!form.cliente_id) { toast.error("Seleziona un cliente"); return; }
    if (!form.modello.trim()) { toast.error("Inserisci marca/modello"); return; }
    try {
      const payload = { ...form, contratto_id: form.contratto_id === "none" ? null : form.contratto_id };
      delete payload.id; delete payload.created_at; delete payload._id; delete payload.cliente_nome; delete payload.ultima_lettura; delete payload.cc_contract_id;
      let devId = editing?.id;
      if (editing) await api.put(`/devices/${editing.id}`, payload);
      else { const { data } = await api.post("/devices", payload); devId = data.id; }
      try { await api.post(`/devices/${devId}/copy-cost-link`, { contract_id: form.cc_contract_id || null }); }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      toast.success(editing ? "Dispositivo aggiornato" : "Dispositivo creato");
      setOpen(false); load(); loadUnbill();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Eliminare ${d.marca} ${d.modello}?`)) return;
    await api.delete(`/devices/${d.id}`); toast.success("Dispositivo eliminato"); load();
  };

  const syncPrt = async () => {
    setSyncing(true);
    const tid = toast.loading("Sincronizzazione Printanista completa in corso (clienti, dispositivi, contatori)...");
    try {
      const { data } = await api.post("/integrations/printanista/sync-full");
      const s = data.summary || {};
      const c = s.clienti || {}; const d = s.dispositivi || {};
      toast.success("Sincronizzazione Printanista completata", { id: tid, description:
        `Account ${c.total_accounts ?? 0} · Clienti riconosciuti ${c.recognized ?? 0} · Nuovi ${c.created ?? 0} · Alias ${c.alias_reused ?? 0} · Device nuovi ${d.new_imported ?? 0} · Collegati ${s.device_collegati_retro ?? 0} · Cambi cliente ${d.client_change_pending ?? 0} · Errori ${c.errors ?? 0}` });
      load(); loadPrt(); loadUnbill();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore sincronizzazione", { id: tid }); }
    finally { setSyncing(false); }
  };

  const excludePrt = async (d) => {
    const rid = d.printanista?.remote_device_id;
    if (!rid) return;
    if (!window.confirm(`Escludere ${d.marca} ${d.modello} dalle future sincronizzazioni Printanista ed eliminarlo da TecSuite?`)) return;
    try {
      await api.post(`/integrations/printanista/devices/${rid}/exclude`, { delete_device: true, reason: "Escluso manualmente" });
      toast.success("Dispositivo escluso da Printanista"); load(); loadPrt();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const restorePrt = async (rid) => {
    try {
      await api.post(`/integrations/printanista/exclusions/${rid}/restore`);
      toast.success("Monitoraggio ripristinato"); loadPrt();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const syncMeters = async () => {
    setMeterSyncing(true);
    const tid = toast.loading("Sincronizzazione contatori in corso...");
    try {
      const { data } = await api.post("/integrations/printanista/meter-sync/run");
      if (data.running) toast.warning("Sincronizzazione contatori già in corso", { id: tid });
      else { const s = data.summary || {}; toast.success("Contatori sincronizzati", { id: tid, description: `Dispositivi ${s.devices_ok} · Nuove letture ${s.new} · Anomalie ${s.anomalies}` }); }
      load(); loadPrt();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail), { id: tid }); }
    finally { setMeterSyncing(false); }
  };

  const confirmClientChange = async (cc) => {
    if (!cc?.link_id) return;    if (!window.confirm(`Spostare il dispositivo al cliente suggerito${cc.cliente_suggerito ? ` "${cc.cliente_suggerito}"` : ""}? L'associazione verrà ricordata nelle sincronizzazioni future.`)) return;
    try {
      await api.post(`/integrations/printanista/device-links/${cc.link_id}/confirm-client-change`);
      toast.success("Dispositivo spostato al nuovo cliente"); load(); loadPrt();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const clientChangeMap = useMemo(() => {
    const m = {};
    (alerts?.client_change || []).forEach((a) => { if (a.device_id) m[a.device_id] = a; });
    return m;
  }, [alerts]);

  const passPrtFilter = (d) => {
    const p = d.printanista;
    const isPrt = (p && p.linked) || d.source === "printanista";
    const daAssociare = isPrt && !d.cliente_id;
    if (prtFilter === "all") return true;
    if (prtFilter === "linked") return !!(p && p.linked);
    if (prtFilter === "not_linked") return !(p && p.linked);
    if (prtFilter === "stale") return !!(p && p.last_seen_at && daysSince(p.last_seen_at) > 10);
    if (prtFilter === "unknown_seen") return !!(p && p.linked && !p.last_seen_at);
    if (prtFilter === "client_change") return !!clientChangeMap[d.id];
    if (prtFilter === "da_associare") return daAssociare;
    if (prtFilter === "senza_cliente") return !d.cliente_id;
    if (prtFilter === "associato") return isPrt && !!d.cliente_id;
    if (prtFilter === "senza_contratto") return noContractSet.has(d.id);
    return true;
  };
  const [dq, setDq] = useState("");
  const matchesDq = (d) => { const t = dq.trim().toLowerCase(); if (!t) return true; return [d.marca, d.modello, d.serial, d.cliente_nome, d.ubicazione].filter(Boolean).join(" ").toLowerCase().includes(t); };
  const visibleDevices = (devices || []).filter(passPrtFilter).filter(matchesDq);
  const [sort, setSort] = useState({ key: "cliente", dir: "asc" });
  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const sortedDevices = useMemo(() => {
    const arr = [...visibleDevices];
    if (sort.key) {
      const dir = sort.dir === "asc" ? 1 : -1;
      arr.sort((a, b) => { const va = devSortVal(a, sort.key), vb = devSortVal(b, sort.key); if (va < vb) return -1 * dir; if (va > vb) return 1 * dir; return 0; });
    }
    return arr;
  }, [visibleDevices, sort]);
  const DW_DEV = { cliente: 180, dispositivo: 160, serial: 140, printanista: 200, ultima_lettura: 140, stato: 110, azioni: 260 };
  const { colW, startResize, totalW } = useColumnResize("devices_col_w", DW_DEV);
  const RH = ({ k }) => <span onMouseDown={(e) => startResize(e, k)} onClick={(e) => e.stopPropagation()} className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60" />;
  const SortHead = ({ k, children, className }) => (
    <TableHead style={{ width: colW[k] }} className={`relative ${className || ""}`}>
      <button type="button" onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 hover:text-foreground" data-testid={`dev-sort-${k}`}>
        {children}
        {sort.key === k ? (sort.dir === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />) : <CaretUpDown size={12} className="opacity-40" />}
      </button>
      <RH k={k} />
    </TableHead>
  );
  const staleCount = (alerts?.counts?.stale_over_10d) || 0;
  const clientChangeCount = (alerts?.counts?.client_change) || 0;
  const daAssociareCount = (devices || []).filter((d) => ((d.printanista && d.printanista.linked) || d.source === "printanista") && !d.cliente_id).length;

  const openReadings = async (d) => {
    setReadDevice(d);
    setTForm({ data_consegna: new Date().toISOString().slice(0, 10), contatore_bn: 0, contatore_colore: 0, tipo_toner: "", note: "" });
    const today = new Date().toISOString().slice(0, 10);
    const [{ data }, { data: td }, sum, hist] = await Promise.all([
      api.get(`/devices/${d.id}/readings`),
      api.get(`/devices/${d.id}/toner-deliveries`),
      api.get(`/devices/${d.id}/meter-summary`).catch(() => ({ data: {} })),
      api.get(`/devices/${d.id}/meter-readings`).catch(() => ({ data: {} })),
    ]);
    // Contatori "Totale pagine mono/colore": stessi codici scelti dal riepilogo (es. PageCountMono/Color)
    const latest = (sum.data || {}).latest || {};
    const codes = { mono: latest.mono?.remote_meter_code || null, colore: latest.colore?.remote_meter_code || null };
    const reads = (hist.data || {}).readings || [];
    setMeterHist(reads);
    setMeterCodes(codes);
    // Precompila col valore delle celle "Totale pagine mono/colore" della scheda contatori Printanista
    // (stesso override per etichetta usato nei badge del dialogo contatori), fallback al riepilogo / storico.
    const meters = (sum.data || {}).meters || [];
    const byLabel = (lbl) => meters.find((m) => (meterLabelIt(m.remote_meter_code) || m.meter_name) === lbl);
    const monoTot = byLabel("Totale pagine mono")?.value ?? latest.mono?.value ?? valueAt(reads, codes.mono, today);
    const coloreTot = byLabel("Totale pagine colore")?.value ?? latest.colore?.value ?? valueAt(reads, codes.colore, today);
    setRForm({
      data_lettura: today,
      contatore_bn: monoTot,
      contatore_colore: coloreTot,
      note: "",
    });
    setTForm((f) => ({ ...f, contatore_bn: monoTot, contatore_colore: coloreTot }));
    setReadings(data);
    setToners(td);
    setReadOpen(true);
  };

  const addToner = async () => {
    if (!tForm.data_consegna) { toast.error("Inserisci la data di consegna"); return; }
    try {
      const { data } = await api.post(`/devices/${readDevice.id}/toner-deliveries`, {
        data_consegna: tForm.data_consegna,
        contatore_bn: Number(tForm.contatore_bn) || 0,
        contatore_colore: Number(tForm.contatore_colore) || 0,
        tipo_toner: tForm.tipo_toner,
        note: tForm.note,
      });
      setToners(data.deliveries);
      toast.success("Consegna toner registrata");
      setTForm((f) => ({ ...f, contatore_bn: 0, contatore_colore: 0, tipo_toner: "", note: "" }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delToner = async (id) => {
    await api.delete(`/toner-deliveries/${id}`);
    const { data } = await api.get(`/devices/${readDevice.id}/toner-deliveries`);
    setToners(data);
  };

  const addReading = async () => {
    if (!rForm.data_lettura) { toast.error("Inserisci la data lettura"); return; }
    try {
      const { data: created } = await api.post(`/devices/${readDevice.id}/readings`, {
        data_lettura: rForm.data_lettura,
        contatore_bn: Number(rForm.contatore_bn) || 0,
        contatore_colore: Number(rForm.contatore_colore) || 0,
        note: rForm.note,
      });
      if (rFile && created?.id) {
        const fd = new FormData();
        fd.append("file", rFile);
        await api.post(`/devices/${readDevice.id}/readings/${created.id}/allegato`, fd);
      }
      toast.success("Lettura registrata");
      const { data } = await api.get(`/devices/${readDevice.id}/readings`);
      setReadings(data);
      setRForm((f) => ({ ...f, contatore_bn: 0, contatore_colore: 0, note: "" }));
      setRFile(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadReadingAllegato = async (r) => {
    try {
      const res = await api.get(`/readings/${r.id}/allegato/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = r.allegato?.nome_originale || "allegato";
      link.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore durante il download dell'allegato"); }
  };

  const delReading = async (id) => {
    await api.delete(`/readings/${id}`);
    const { data } = await api.get(`/devices/${readDevice.id}/readings`);
    setReadings(data); load();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12">
        <PageHeader
          title="Dispositivi"
          subtitle="Stampanti a costo copia e dispositivi gestiti (NinjaOne RMM)"
        />

        <Tabs value={deviceTab} onValueChange={setDeviceTab} className="mt-2">
          <TabsList className="rounded-sm mb-4">
            <TabsTrigger value="stampanti" data-testid="tab-stampanti"><Gauge size={15} className="mr-1.5" />Stampanti / Costo copia</TabsTrigger>
            <TabsTrigger value="ninjaone" data-testid="tab-ninjaone"><Desktop size={15} className="mr-1.5" />NinjaOne RMM</TabsTrigger>
            <TabsTrigger value="zyxel" data-testid="tab-zyxel"><WifiHigh size={15} className="mr-1.5" />Zyxel Nebula</TabsTrigger>
            <TabsTrigger value="rendicontazioni" data-testid="tab-rendicontazioni"><Receipt size={15} className="mr-1.5" />Rendicontazioni</TabsTrigger>
          </TabsList>

          <TabsContent value="stampanti">
            {(staleCount > 0 || clientChangeCount > 0 || daAssociareCount > 0 || noContractSet.size > 0 || unbill.n_dispositivi > 0) && (
              <div className="flex flex-wrap gap-2 mb-3">
                {noContractSet.size > 0 && (
                  <button onClick={() => setPrtFilter("senza_contratto")} className="flex items-center gap-2 rounded-sm bg-purple-50 border border-purple-200 text-purple-800 text-sm px-3 py-2 font-medium" data-testid="cc-no-contract-banner">
                    <Receipt size={16} weight="fill" /> {noContractSet.size} fotocopiatric{noContractSet.size === 1 ? "e associata a un cliente non ha" : "i associate a un cliente non hanno"} un contratto costo copia — filtra
                  </button>
                )}
                {unbill.n_dispositivi > 0 && (
                  <div className="flex items-center gap-2 rounded-sm bg-slate-50 border border-slate-200 text-slate-700 text-sm px-3 py-2" data-testid="cc-unbillable-banner">
                    <FileText size={16} weight="fill" /> Pagine attualmente non rendicontabili: {unbill.totale_copie_mono} mono · {unbill.totale_copie_colore} colore ({unbill.n_dispositivi} dispositivi)
                  </div>
                )}
                {daAssociareCount > 0 && (
                  <button onClick={() => setPrtFilter("da_associare")} className="flex items-center gap-2 rounded-sm bg-orange-50 border border-orange-200 text-orange-800 text-sm px-3 py-2 font-medium" data-testid="prt-da-associare-banner">
                    <LinkSimple size={16} weight="fill" /> {daAssociareCount} dispositiv{daAssociareCount === 1 ? "o Printanista deve" : "i Printanista devono"} essere associat{daAssociareCount === 1 ? "o" : "i"} a un'anagrafica TecSuite — filtra
                  </button>
                )}
                {staleCount > 0 && (
                  <button onClick={() => setPrtFilter("stale")} className="flex items-center gap-2 rounded-sm bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2" data-testid="prt-stale-banner">
                    <WarningCircle size={16} weight="fill" /> {staleCount} dispositiv{staleCount === 1 ? "o" : "i"} non rilevat{staleCount === 1 ? "o" : "i"} da oltre 10 giorni — filtra
                  </button>
                )}
                {clientChangeCount > 0 && (
                  <button onClick={() => setPrtFilter("client_change")} className="flex items-center gap-2 rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2" data-testid="prt-clientchange-banner">
                    <WarningCircle size={16} weight="fill" /> {clientChangeCount} cambio cliente Printanista da confermare — filtra
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={dq} onChange={(e) => setDq(e.target.value)} placeholder="Cerca marca, modello, seriale, cliente…" className="pl-8 rounded-sm bg-white w-72" data-testid="device-search" />
                </div>
                <ClientCombobox clients={clients} value={fCliente} width="w-56" onChange={setFCliente} extraOptions={[{ value: "all", label: "Tutti i clienti" }]} placeholder="Cliente" testid="device-filter-cliente" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={syncPrt} disabled={syncing} className="rounded-sm gap-2" data-testid="prt-sync-devices-btn"><ArrowsClockwise size={16} className={syncing ? "animate-spin" : ""} /> Sincronizza Printanista</Button>
                <Button variant="outline" onClick={syncMeters} disabled={meterSyncing} className="rounded-sm gap-2" data-testid="prt-sync-meters-btn"><Gauge size={16} className={meterSyncing ? "animate-spin" : ""} /> Sincronizza contatori</Button>
                <Button variant="outline" onClick={() => setSuppliesOpen(true)} className="rounded-sm gap-2" data-testid="prt-supplies-dashboard-btn"><Drop size={16} /> Consumabili</Button>
                <Button data-testid="new-device-btn" onClick={openNew} className="rounded-sm gap-2"><Plus size={16} weight="bold" /> Nuovo dispositivo</Button>
              </div>
            </div>

            <Card className="rounded-sm border-border shadow-none">
          <div className="overflow-x-auto">
          <Table style={{ tableLayout: "fixed", width: totalW }} className="[&_td]:overflow-hidden [&_th]:overflow-hidden">
            <TableHeader>
              <TableRow>
                <SortHead k="cliente">Cliente</SortHead>
                <SortHead k="dispositivo">Dispositivo</SortHead>
                <SortHead k="serial">Serial</SortHead>
                <TableHead style={{ width: colW.printanista }} className="relative">Fonte<RH k="printanista" /></TableHead>
                <SortHead k="ultima_lettura">Ultima lettura</SortHead>
                <SortHead k="stato">Stato</SortHead>
                <TableHead style={{ width: colW.azioni }} className="relative text-right">Azioni<RH k="azioni" /></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedDevices?.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Nessun dispositivo.</TableCell></TableRow>}
              {sortedDevices?.map((d) => {
                const p = d.printanista;
                const dseen = p && p.last_seen_at ? daysSince(p.last_seen_at) : null;
                const stale = dseen != null && dseen > 10;
                const cc = clientChangeMap[d.id];
                return (
                <TableRow key={d.id} data-testid="device-row" className="hover:bg-secondary">
                  <TableCell className="text-muted-foreground">
                    {d.cliente_nome || (((p && p.linked) || d.source === "printanista")
                      ? <Badge variant="outline" className="rounded-sm text-[10px] bg-orange-100 text-orange-800 border-orange-200" data-testid={`prt-da-associare-badge-${d.id}`}>Da associare</Badge>
                      : "—")}
                  </TableCell>
                  <TableCell className="font-medium">{d.marca} {d.modello}</TableCell>
                  <TableCell className="font-mono text-xs">{d.serial || "—"}</TableCell>
                  <TableCell>
                    {p && p.linked ? (
                      <div className="flex flex-wrap gap-1" data-testid={`prt-badges-${d.id}`}>
                        <Badge variant="outline" className="rounded-sm text-[10px] bg-orange-50 text-orange-700 border-orange-200">Printanista</Badge>
                        {p.managed && <Badge variant="outline" className="rounded-sm text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Gestito</Badge>}
                        {stale && <Badge variant="outline" className="rounded-sm text-[10px] bg-red-100 text-red-800 border-red-200" data-testid={`prt-stale-badge-${d.id}`}>&gt;10gg ({dseen}g)</Badge>}
                        {cc && <Badge variant="outline" className="rounded-sm text-[10px] bg-amber-100 text-amber-800 border-amber-200" data-testid={`prt-cc-badge-${d.id}`}>Cambio cliente</Badge>}
                        {d.supplies_summary && d.supplies_summary.worst_level && !["regolare", "sconosciuto"].includes(d.supplies_summary.worst_level) && (
                          <Badge variant="outline" className={`rounded-sm text-[10px] ${d.supplies_summary.worst_level === "critico" ? "bg-red-100 text-red-800 border-red-200" : d.supplies_summary.worst_level === "in_esaurimento" ? "bg-orange-100 text-orange-800 border-orange-200" : "bg-yellow-100 text-yellow-800 border-yellow-200"}`} data-testid={`prt-supply-badge-${d.id}`} title="Consumabile">{d.supplies_summary.worst_level.replace("_", " ")}</Badge>
                        )}
                      </div>
                    ) : <Badge variant="outline" className="rounded-sm text-[10px] bg-slate-100 text-slate-600 border-slate-200" data-testid={`source-manuale-${d.id}`}>Manuale</Badge>}
                  </TableCell>
                  <TableCell className="tabular-nums">{d.ultima_lettura ? fmtDate(d.ultima_lettura.data_lettura) : "—"}</TableCell>
                  <TableCell><StatusBadge status={d.stato} /></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" data-testid="readings-btn" onClick={() => openReadings(d)} className="h-8 w-8 text-primary" title="Letture"><Gauge size={16} weight="bold" /></Button>
                    {p && p.linked && <Button variant="ghost" size="icon" onClick={() => setMeterDevice(d)} className="h-8 w-8 text-orange-600" title="Contatori Printanista" data-testid={`prt-meters-${d.id}`}><Gauge size={16} /></Button>}
                    <Button variant="ghost" size="icon" onClick={() => openEdit(d)} className="h-8 w-8" title="Modifica"><PencilSimple size={16} /></Button>
                    {p && p.linked && <Button variant="ghost" size="icon" onClick={() => excludePrt(d)} className="h-8 w-8 text-amber-600" title="Escludi da Printanista" data-testid={`prt-exclude-${d.id}`}><Prohibit size={16} /></Button>}
                    {cc && <Button variant="outline" size="sm" onClick={() => confirmClientChange(cc)} className="h-8 rounded-sm text-xs ml-1" data-testid={`prt-confirm-cc-${d.id}`}>Sposta e associa</Button>}
                    {d.cliente_id && noContractSet.has(d.id) && (
                      <Link to={`/contratti?tab=noleggio_costo_copia&device_id=${d.id}&cliente_id=${d.cliente_id}&cc_new=1`} className="inline-flex items-center h-8 rounded-sm border border-purple-200 text-purple-700 hover:bg-purple-50 text-xs ml-1 gap-1 px-3 font-medium select-none" data-testid={`cc-row-create-${d.id}`} title="Crea o associa un contratto costo copia per questa stampante"><Receipt size={14} /> Crea/associa contratto</Link>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => { setStmtDeviceId(d.id); setStmtDeviceLabel(`${d.marca || ""} ${d.modello || ""} · ${d.serial || ""}`.trim()); setDeviceTab("rendicontazioni"); }} className="h-8 w-8 text-emerald-700" title="Rendicontazioni" data-testid={`ccs-device-btn-${d.id}`}><Receipt size={16} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(d)} className="h-8 w-8 text-muted-foreground hover:text-destructive" title="Elimina"><Trash size={16} /></Button>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        </Card>

            {exclusions.length > 0 && (
              <Card className="rounded-sm border-border shadow-none mt-4 p-4" data-testid="prt-exclusions-section">
                <div className="flex items-center gap-2 mb-3"><Prohibit size={16} className="text-amber-600" /><h3 className="font-heading font-semibold text-sm">Esclusi da Printanista ({exclusions.length})</h3></div>
                <div className="divide-y divide-border/60">
                  {exclusions.map((e) => (
                    <div key={e.remote_device_id} className="flex items-center justify-between py-2 text-sm" data-testid={`prt-excl-row-${e.remote_device_id}`}>
                      <div className="text-muted-foreground">Remote ID {e.remote_device_id}{e.reason ? ` · ${e.reason}` : ""}</div>
                      <Button variant="outline" size="sm" className="rounded-sm gap-1.5 h-8" onClick={() => restorePrt(e.remote_device_id)} data-testid={`prt-restore-${e.remote_device_id}`}><ArrowCounterClockwise size={14} /> Ripristina monitoraggio</Button>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="ninjaone">
            <NinjaDevices />
          </TabsContent>

          <TabsContent value="zyxel">
            <ZyxelDevices />
          </TabsContent>
          <TabsContent value="rendicontazioni">
            {stmtDeviceId && (
              <div className="mb-3 flex items-center gap-2 text-xs bg-secondary/50 rounded-sm px-3 py-2" data-testid="ccs-device-filter-banner">
                Filtro dispositivo attivo{stmtDeviceLabel ? `: ${stmtDeviceLabel}` : ""}
                <Button size="sm" variant="ghost" className="h-6" onClick={() => setStmtDeviceId(null)} data-testid="ccs-device-filter-reset">Mostra tutte</Button>
              </div>
            )}
            <CopyCostStatements key={stmtDeviceId || "global"} deviceId={stmtDeviceId} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Device dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifica dispositivo" : "Nuovo dispositivo"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider">Cliente *</Label>
              <div className="mt-1"><ClientCombobox clients={clients} value={form.cliente_id} onChange={(v) => { setForm((f) => ({ ...f, cliente_id: v, contratto_id: "none", cc_contract_id: "" })); loadCcContracts(v); }} placeholder="Seleziona" testid="device-cliente" /></div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Contratto costo copia</Label>
              <Select value={form.cc_contract_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, cc_contract_id: v === "none" ? "" : v }))}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="device-cc-contract"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {ccContracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.numero}{c.titolo && c.titolo !== c.numero ? ` · ${c.titolo}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.cliente_id && ccContracts.length === 0 && <p className="mt-1 text-[11px] text-muted-foreground">Nessun contratto costo copia per questo cliente: creane uno dal modulo Noleggio e Costo Copia.</p>}
              {form.cc_contract_id && <p className="mt-1 text-[11px] text-muted-foreground">Condizioni economiche (contatori, canone) da completare nel modulo Noleggio e Costo Copia.</p>}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Marca</Label>
              <Input value={form.marca} onChange={set("marca")} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Modello *</Label>
              <Input data-testid="device-modello" value={form.modello} onChange={set("modello")} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Serial</Label>
              <Input value={form.serial} onChange={set("serial")} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Ubicazione</Label>
              <Input value={form.ubicazione} onChange={set("ubicazione")} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm((f) => ({ ...f, tipo: v }))}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="bn">Bianco/Nero</SelectItem><SelectItem value="colore">Colore</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Stato</Label>
              <Select value={form.stato} onValueChange={(v) => setForm((f) => ({ ...f, stato: v }))}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="attivo">Attivo</SelectItem><SelectItem value="dismesso">Dismesso</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Annulla</Button>
            <Button data-testid="save-device-btn" onClick={save} className="rounded-sm">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Readings dialog */}
      <Dialog open={readOpen} onOpenChange={setReadOpen}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Letture contatori — {readDevice?.marca} {readDevice?.modello}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-12 gap-2 items-end bg-secondary p-3 rounded-sm">
            <div className="col-span-3">
              <Label className="text-[10px] uppercase">Data lettura</Label>
              <Input type="date" value={rForm.data_lettura} onChange={(e) => { const dt = e.target.value; setRForm((f) => ({ ...f, data_lettura: dt, contatore_bn: valueAt(meterHist, meterCodes.mono, dt), contatore_colore: valueAt(meterHist, meterCodes.colore, dt) })); }} className="rounded-sm bg-white h-9" data-testid="reading-date" />
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] uppercase">Contatore B/N</Label>
              <Input type="number" value={rForm.contatore_bn} onChange={(e) => setRForm((f) => ({ ...f, contatore_bn: e.target.value }))} className="rounded-sm bg-white h-9" data-testid="reading-bn" />
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] uppercase">Contatore colore</Label>
              <Input type="number" value={rForm.contatore_colore} onChange={(e) => setRForm((f) => ({ ...f, contatore_colore: e.target.value }))} className="rounded-sm bg-white h-9" data-testid="reading-colore" />
            </div>
            <div className="col-span-3">
              <Button onClick={addReading} data-testid="add-reading-btn" className="rounded-sm w-full">Registra lettura</Button>
            </div>
            <div className="col-span-12 flex items-center gap-2">
              <input id="reading-file-input" type="file" className="hidden" onChange={(e) => setRFile(e.target.files?.[0] || null)} data-testid="reading-file-input" />
              <Button type="button" variant="outline" size="sm" className="rounded-sm h-8" onClick={() => document.getElementById("reading-file-input").click()} data-testid="reading-file-btn"><Paperclip size={14} className="mr-1.5" />Allega file</Button>
              {rFile && <span className="text-xs text-muted-foreground truncate max-w-xs" data-testid="reading-file-name">{rFile.name}</span>}
            </div>
          </div>
          {(() => {
            if (!readDevice || meterHist.length === 0 || (!meterCodes.mono && !meterCodes.colore)) return null;
            const dates = readingDates(meterHist, meterCodes);
            if (dates.has(rForm.data_lettura)) return null;
            const last = [...dates].sort().pop();
            if (!last) return null;
            return (
              <div className="flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 mt-2" data-testid="reading-no-today-alert">
                <WarningCircle size={15} weight="fill" className="shrink-0" />
                <span>Nessuna lettura remota per la data selezionata. Ultima disponibile: <b>{fmtDate(last)}</b> (mono {valueAt(meterHist, meterCodes.mono, last)} · colore {valueAt(meterHist, meterCodes.colore, last)}). Usa quella data o inserisci i valori a mano.</span>
                <Button size="sm" variant="outline" className="rounded-sm h-7 ml-auto shrink-0" data-testid="reading-use-last-date"
                  onClick={() => setRForm((f) => ({ ...f, data_lettura: last, contatore_bn: valueAt(meterHist, meterCodes.mono, last), contatore_colore: valueAt(meterHist, meterCodes.colore, last) }))}>
                  Usa {fmtDate(last)}
                </Button>
              </div>
            );
          })()}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Contatore B/N</TableHead>
                <TableHead className="text-right">Contatore colore</TableHead>
                <TableHead className="text-right">Copie periodo B/N</TableHead>
                <TableHead className="text-right">Copie periodo colore</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {readings.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Nessuna lettura registrata.</TableCell></TableRow>}
              {readings.map((r) => (
                <TableRow key={r.id} data-testid="reading-row">
                  <TableCell className="tabular-nums">{fmtDate(r.data_lettura)}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.contatore_bn}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.contatore_colore}</TableCell>
                  <TableCell className="text-right tabular-nums text-primary font-medium">{r.delta ? r.delta.copie_bn : "—"}</TableCell>
                  <TableCell className="text-right tabular-nums text-primary font-medium">{r.delta ? r.delta.copie_colore : "—"}</TableCell>
                  <TableCell className="text-right">
                    {r.allegato && <Button variant="ghost" size="icon" onClick={() => downloadReadingAllegato(r)} className="h-8 w-8 text-primary" title={r.allegato.nome_originale} data-testid="reading-allegato-download"><Paperclip size={14} /></Button>}
                    <Button variant="ghost" size="icon" onClick={() => delReading(r.id)} className="h-8 w-8 text-muted-foreground hover:text-destructive"><Trash size={14} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="mt-6 pt-5 border-t border-border">
            <div className="flex items-center gap-2 mb-1">
              <Drop size={18} weight="fill" className="text-primary" />
              <h3 className="font-heading font-semibold text-base">Logbook consegna toner</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Registra ogni consegna toner con la lettura del contatore a quel giorno. Alla consegna successiva viene calcolato quante copie ha fatto la macchina tra le due consegne.</p>
            <div className="grid grid-cols-12 gap-2 items-end bg-secondary p-3 rounded-sm">
              <div className="col-span-2">
                <Label className="text-[10px] uppercase">Data consegna</Label>
                <Input type="date" value={tForm.data_consegna} onChange={(e) => { const dt = e.target.value; setTForm((f) => ({ ...f, data_consegna: dt, contatore_bn: valueAt(meterHist, meterCodes.mono, dt), contatore_colore: valueAt(meterHist, meterCodes.colore, dt) })); }} className="rounded-sm bg-white h-9" data-testid="toner-date" />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase">Contatore B/N</Label>
                <Input type="number" value={tForm.contatore_bn} onChange={(e) => setTForm((f) => ({ ...f, contatore_bn: e.target.value }))} className="rounded-sm bg-white h-9" data-testid="toner-bn" />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase">Contatore colore</Label>
                <Input type="number" value={tForm.contatore_colore} onChange={(e) => setTForm((f) => ({ ...f, contatore_colore: e.target.value }))} className="rounded-sm bg-white h-9" data-testid="toner-colore" />
              </div>
              <div className="col-span-3">
                <Label className="text-[10px] uppercase">Tipo toner</Label>
                <Input value={tForm.tipo_toner} onChange={(e) => setTForm((f) => ({ ...f, tipo_toner: e.target.value }))} placeholder="es. Nero, Ciano..." className="rounded-sm bg-white h-9" data-testid="toner-tipo" />
              </div>
              <div className="col-span-3">
                <Button onClick={addToner} data-testid="add-toner-btn" className="rounded-sm w-full">Registra consegna</Button>
              </div>
            </div>
            {(() => {
              if (!readDevice || meterHist.length === 0 || (!meterCodes.mono && !meterCodes.colore)) return null;
              const dates = readingDates(meterHist, meterCodes);
              if (dates.has(tForm.data_consegna)) return null;
              const last = [...dates].sort().pop();
              if (!last) return null;
              return (
                <div className="flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 mt-2" data-testid="toner-no-today-alert">
                  <WarningCircle size={15} weight="fill" className="shrink-0" />
                  <span>Nessuna lettura remota per la data selezionata. Ultima disponibile: <b>{fmtDate(last)}</b> (mono {valueAt(meterHist, meterCodes.mono, last)} · colore {valueAt(meterHist, meterCodes.colore, last)}). Usa quella data o inserisci i valori a mano.</span>
                  <Button size="sm" variant="outline" className="rounded-sm h-7 ml-auto shrink-0" data-testid="toner-use-last-date"
                    onClick={() => setTForm((f) => ({ ...f, data_consegna: last, contatore_bn: valueAt(meterHist, meterCodes.mono, last), contatore_colore: valueAt(meterHist, meterCodes.colore, last) }))}>
                    Usa {fmtDate(last)}
                  </Button>
                </div>
              );
            })()}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data consegna</TableHead>
                  <TableHead>Toner</TableHead>
                  <TableHead className="text-right">Contatore B/N</TableHead>
                  <TableHead className="text-right">Contatore colore</TableHead>
                  <TableHead className="text-right">Copie dalla consegna precedente</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {toners.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Nessuna consegna toner registrata.</TableCell></TableRow>}
                {toners.map((t) => (
                  <TableRow key={t.id} data-testid="toner-row">
                    <TableCell className="tabular-nums">{fmtDate(t.data_consegna)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{t.tipo_toner || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{t.contatore_bn}</TableCell>
                    <TableCell className="text-right tabular-nums">{t.contatore_colore}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium text-primary">
                      {t.copie_dal_precedente
                        ? <>{t.copie_dal_precedente.totale} <span className="text-[11px] text-muted-foreground font-normal">(B/N {t.copie_dal_precedente.bn} · Col {t.copie_dal_precedente.colore})</span></>
                        : <span className="text-muted-foreground font-normal">prima consegna</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => delToner(t.id)} className="h-8 w-8 text-muted-foreground hover:text-destructive"><Trash size={14} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
      <PrintanistaMeterDialog open={!!meterDevice} onOpenChange={(v) => !v && setMeterDevice(null)} device={meterDevice} />
      <PrintanistaSuppliesDashboard open={suppliesOpen} onOpenChange={setSuppliesOpen} />
    </InternalLayout>
  );
}
